/* NEXLETE — product page behaviour.

   Variant resolution runs off a JSON payload rendered once by Liquid rather
   than a section re-render per click. On a 21-variant product a server round
   trip per selection is both slow and loses keyboard focus.

   Everything here degrades: with JS off the form still posts to /cart/add and
   Shopify resolves the variant from the option names, so the page remains
   purchasable. */

(function () {
  const root = document.querySelector("[data-nx-pdp]");
  if (!root) return;

  const payload = root.querySelector("[data-nx-variants]");
  if (!payload) return;

  let variants = [];
  try {
    variants = JSON.parse(payload.textContent);
  } catch (e) {
    return; // malformed payload: leave the native form alone
  }

  const form = root.querySelector("[data-nx-atc-form]");
  const idField = root.querySelector("[data-nx-variant-id]");
  const priceEl = root.querySelector("[data-nx-price]");
  const atc = root.querySelector("[data-nx-atc]");
  const atcLabel = root.querySelector("[data-nx-atc-label]");
  const stockEl = root.querySelector("[data-nx-stock]");
  const frames = [...root.querySelectorAll("[data-nx-frame]")];
  const thumbs = [...root.querySelectorAll("[data-nx-thumb]")];
  const thumbStrip = root.querySelector("[data-nx-thumbs]");
  const noPhotoNote = root.querySelector("[data-nx-nophoto]");
  const thumbItems = thumbs.map((t) => t.closest("li") || t);
  const defaultLabel = atcLabel ? atcLabel.textContent.trim() : "Add to bag";

  /* ------------------------------------------------------------- gallery

     The gallery shows one colourway at a time. Each frame carries the colour
     it was shot in (Liquid reads it out of the media's alt text, which is the
     only per-media field there is), and choosing a colour hides the rest.

     Indices here are the media's own position, not a position in the visible
     subset, so they stay stable as frames come and go. */
  function frameIndex(el) {
    return Number(el.dataset.nxFrame !== undefined ? el.dataset.nxFrame : el.dataset.nxThumb);
  }

  function showFrame(i) {
    frames.forEach((f) => f.classList.toggle("is-active", frameIndex(f) === i));
    thumbs.forEach((t) => t.classList.toggle("is-active", frameIndex(t) === i));
  }

  thumbs.forEach((t) => t.addEventListener("click", () => showFrame(frameIndex(t))));

  /* Returns the frames now on show. Falls back to the whole gallery when a
     colour has no photography of its own — Radiance is only shot in Light
     Blue, and an empty stage would be far worse than the full set. */
  function filterGallery(colour) {
    const showAll = (fellBack) => {
      frames.forEach((f) => (f.hidden = false));
      thumbItems.forEach((li) => (li.hidden = false));
      if (noPhotoNote) noPhotoNote.hidden = !fellBack;
      return frames;
    };
    if (!colour) return showAll(false);

    const hits = frames.filter((f) => (f.dataset.nxColour || "") === colour);
    // Falling back is a claim the page must not make silently: the shopper
    // has chosen a colour and is about to be shown a different one.
    if (!hits.length) return showAll(true);

    frames.forEach((f) => (f.hidden = (f.dataset.nxColour || "") !== colour));
    thumbItems.forEach((li) => (li.hidden = (li.dataset.nxColour || "") !== colour));
    if (noPhotoNote) noPhotoNote.hidden = true;
    return hits;
  }

  function currentColour() {
    const group = root.querySelector("[data-nx-colour-group]");
    if (!group) return null;
    const checked = group.querySelector("input[data-nx-option]:checked");
    return checked ? checked.value.trim().toLowerCase() : null;
  }

  let shownColour;
  function syncGallery(v) {
    const colour = currentColour();
    const colourChanged = colour !== shownColour;
    shownColour = colour;

    const visible = filterGallery(colour);
    if (thumbStrip) thumbStrip.hidden = visible.length < 2;

    /* Only re-point the stage when it has to move: on a colour change, or
       when the frame on show has just been hidden. Changing SIZE must not
       throw away the angle the shopper picked. */
    const active = frames.find((f) => f.classList.contains("is-active"));
    if (!colourChanged && active && !active.hidden) return;

    const featured =
      v && typeof v.media_index === "number" ? frames[v.media_index] : null;
    const target = featured && !featured.hidden ? featured : visible[0];
    if (target) showFrame(frameIndex(target));
  }

  /* ------------------------------------------------------------- options */
  function selected() {
    const groups = [...root.querySelectorAll("[data-nx-option-group]")];
    return groups.map((g) => {
      const checked = g.querySelector("input[data-nx-option]:checked");
      return checked ? checked.value : null;
    });
  }

  function findVariant(opts) {
    return variants.find((v) =>
      opts.every((o, i) => o === null || v.options[i] === o)
    );
  }

  /* Cross off sizes that do not exist in the chosen colour. Kept visible and
     struck through: a shopper should see the full run and understand their
     size exists, just not in that colourway. */
  function markUnavailable() {
    const opts = selected();
    root.querySelectorAll("[data-nx-option-group]").forEach((group, gi) => {
      group.querySelectorAll("input[data-nx-option]").forEach((input) => {
        const trial = opts.slice();
        trial[gi] = input.value;
        const match = variants.find((v) =>
          trial.every((o, i) => o === null || v.options[i] === o)
        );
        const label = input.closest(".nx-size, .nx-swatch");
        if (label) label.classList.toggle("is-unavailable", !(match && match.available));
      });
    });
  }

  function apply() {
    const opts = selected();
    const v = findVariant(opts);

    root.querySelectorAll("[data-nx-chosen]").forEach((el, i) => {
      if (opts[i]) el.textContent = opts[i];
    });

    markUnavailable();

    if (!v) {
      if (atc) atc.disabled = true;
      if (atcLabel) atcLabel.textContent = "Unavailable";
      syncGallery(null); // the colour is still a real choice; keep it on show
      return;
    }

    if (idField) idField.value = v.id;

    if (priceEl) {
      priceEl.innerHTML =
        v.price_formatted +
        (v.compare_at && v.compare_at > v.price
          ? ' <s class="nx-pdp__was">' + v.compare_formatted + "</s>"
          : "");
    }

    if (atc) atc.disabled = !v.available;
    if (atcLabel) atcLabel.textContent = v.available ? defaultLabel : "Sold out";

    syncGallery(v);

    // Keep the URL shareable — a pasted link should open on the same variant.
    if (window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", v.id);
      window.history.replaceState({}, "", url);
    }

    if (stockEl) {
      stockEl.hidden = true;
      stockEl.removeAttribute("data-state");
    }
  }

  root.querySelectorAll("input[data-nx-option]").forEach((input) =>
    input.addEventListener("change", apply)
  );
  apply();

  /* --------------------------------------------------------- add to cart */
  if (form) {
    form.addEventListener("submit", async (e) => {
      // Let the native POST happen if fetch is unavailable for any reason.
      if (!window.fetch) return;
      e.preventDefault();
      if (!atc || atc.disabled) return;

      const original = atcLabel ? atcLabel.textContent : "";
      if (atcLabel) atcLabel.textContent = "Adding…";
      atc.disabled = true;

      try {
        const res = await fetch(window.Shopify?.routes?.root
          ? window.Shopify.routes.root + "cart/add.js"
          : "/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: Number(idField.value), quantity: 1 }] }),
        });
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.description || data.message || "Could not add to bag");
        }

        if (atcLabel) atcLabel.textContent = "Added";
        refreshCartCount();
        // Let the drawer take over the feedback. Dispatched rather than called
        // directly so the PDP has no hard dependency on the drawer existing.
        document.dispatchEvent(new CustomEvent("nx:cart:added"));
        setTimeout(() => {
          if (atcLabel) atcLabel.textContent = original || defaultLabel;
          atc.disabled = false;
        }, 1600);
      } catch (err) {
        if (stockEl) {
          stockEl.hidden = false;
          stockEl.setAttribute("data-state", "error");
          stockEl.textContent = err.message;
        }
        if (atcLabel) atcLabel.textContent = original || defaultLabel;
        atc.disabled = false;
      }
    });
  }

  async function refreshCartCount() {
    try {
      const r = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      const c = await r.json();
      document.querySelectorAll("[data-nx-cart-count]").forEach((el) => {
        el.textContent = c.item_count;
      });
    } catch (e) {
      /* a stale badge is not worth breaking the flow over */
    }
  }

  /* --------------------------------------------------------- size guide */
  const guide = root.querySelector("[data-nx-sizeguide]");
  if (guide) {
    const open = () => {
      guide.hidden = false;
      document.documentElement.style.overflow = "hidden";
    };
    const close = () => {
      guide.hidden = true;
      document.documentElement.style.overflow = "";
    };
    root.querySelectorAll("[data-nx-sizeguide-open]").forEach((b) =>
      b.addEventListener("click", open)
    );
    root.querySelectorAll("[data-nx-sizeguide-close]").forEach((b) =>
      b.addEventListener("click", close)
    );
    guide.addEventListener("click", (e) => {
      if (e.target === guide) close();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !guide.hidden) close();
    });
  }
})();
